import type { ColorMode, ZonesMapConfig } from '@chukua/common';
import { Marker, Popup, type Map as MapLibreMap } from 'maplibre-gl';
import { pickLogo } from '../../../../../shared/features/organization/policies/pick-logo';

export type CompanyOnMap = NonNullable<ZonesMapConfig['company']>;

/** "Index Cargo" → "IC": the badge's stand-in until the company has a logo. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
}

function badge(company: CompanyOnMap, mode: ColorMode): HTMLElement {
  const element = document.createElement('div');
  element.className = 'company-map-marker';
  element.setAttribute('role', 'img');
  element.setAttribute('aria-label', company.name);
  const logo = pickLogo(company, mode);
  if (!logo) {
    element.textContent = initials(company.name);
    return element;
  }
  const image = document.createElement('img');
  image.src = logo;
  image.alt = '';
  element.append(image);
  return element;
}

/**
 * The company's own place on the Zones map: its logo in a round badge (styles.css), named on hover like a
 * zone is, so the zones read against where the company itself is. Returns the teardown.
 */
export function addCompanyMarker(map: MapLibreMap, company: CompanyOnMap, mode: ColorMode): () => void {
  const position: [number, number] = [company.location.lng, company.location.lat];
  const element = badge(company, mode);
  const label = new Popup({ closeButton: false, closeOnClick: false, offset: 22, className: 'zone-map-label' }).setText(company.name);
  const marker = new Marker({ element, anchor: 'center' }).setLngLat(position).addTo(map);
  element.addEventListener('mouseenter', () => label.setLngLat(position).addTo(map));
  element.addEventListener('mouseleave', () => label.remove());
  return () => {
    label.remove();
    marker.remove();
  };
}
